/**
 * Role Management Page
 * Admin interface for managing roles and permissions
 * Supports both system-wide roles and tenant-specific roles
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import useAsyncData from '../../hooks/useAsyncData';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import usePagination from '../../hooks/usePagination';
import useTableSort from '../../hooks/useTableSort';
import { useTenant } from '../../contexts/TenantContext';
import { useNotification } from '../../contexts/NotificationContext';
import RolePermissionMatrix from '../../components/admin/RolePermissionMatrix';

const RoleManagement = () => {
  const notify = useNotification();
  const { tenant } = useTenant();
  const [activeTab, setActiveTab] = useState(0); // 0 = System, 1 = Tenant

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, role: null });
  const [permissionDialog, setPermissionDialog] = useState({ open: false, role: null });
  const [cloneDialog, setCloneDialog] = useState({ open: false, role: null });
  const [actionLoading, setActionLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
  });

  const isMasterTenant = tenant?.isMaster === true;

  const confirm = useConfirmDialog();
  const pagination = usePagination({ defaultRowsPerPage: 10 });
  const sort = useTableSort({ onSortChange: pagination.resetPage });

  const { data: roles, loading, reload: loadRoles } = useAsyncData(
    async () => {
      try {
        return await apiGet('/admin/roles');
      } catch (err) {
        if (err.response?.status === 403) {
          throw new Error('You do not have permission to manage roles. Admin access required.');
        }
        throw new Error('Failed to load roles: ' + (err.message || 'Unknown error'));
      }
    },
    { defaultValue: [], onError: notify.error }
  );

  // Reset pagination when switching tabs
  useEffect(() => {
    pagination.resetPage();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter roles by tab
  const systemRoles = roles.filter(r => r.tenant_id === null);
  const tenantRoles = roles.filter(r => r.tenant_id !== null);

  const displayedRoles = activeTab === 0 ? systemRoles : tenantRoles;

  const handleCreateRole = async () => {
    try {
      setActionLoading(true);

      await apiPost('/admin/roles', {
        code: formData.code.toLowerCase().replace(/\s+/g, '_'),
        name: formData.name,
        description: formData.description,
      });

      notify.success('Role created successfully');
      setCreateDialog(false);
      setFormData({ code: '', name: '', description: '' });
      loadRoles();
    } catch (err) {
      notify.error('Failed to create role: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    try {
      setActionLoading(true);

      await apiPut(`/admin/roles/${editDialog.role.id}`, {
        name: formData.name,
        description: formData.description,
      });

      notify.success('Role updated successfully');
      setEditDialog({ open: false, role: null });
      loadRoles();
    } catch (err) {
      notify.error('Failed to update role: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloneRole = async () => {
    try {
      setActionLoading(true);

      await apiPost(`/admin/roles/${cloneDialog.role.id}/clone`, {
        code: formData.code.toLowerCase().replace(/\s+/g, '_'),
        name: formData.name,
        description: formData.description,
      });

      notify.success('Role cloned successfully');
      setCloneDialog({ open: false, role: null });
      setFormData({ code: '', name: '', description: '' });
      loadRoles();
    } catch (err) {
      notify.error('Failed to clone role: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRole = async (role) => {
    try {
      setActionLoading(true);

      await apiDelete(`/admin/roles/${role.id}`);

      notify.success('Role deleted successfully');
      loadRoles();
    } catch (err) {
      notify.error('Failed to delete role: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteConfirm = (role) => {
    confirm.show({
      title: 'Delete Role',
      message: `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      onConfirm: () => handleDeleteRole(role),
    });
  };

  const handleSavePermissions = async (roleId, permissions) => {
    try {
      setActionLoading(true);

      await apiPut(`/admin/roles/${roleId}/permissions`, { permissions });

      notify.success('Permissions updated successfully');
      setPermissionDialog({ open: false, role: null });
      loadRoles();
    } catch (err) {
      notify.error('Failed to update permissions: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = (role) => {
    setFormData({
      code: role.code,
      name: role.name,
      description: role.description || '',
    });
    setEditDialog({ open: true, role });
  };

  const openCloneDialog = (role) => {
    setFormData({
      code: role.code + '_copy',
      name: role.name + ' (Copy)',
      description: role.description || '',
    });
    setCloneDialog({ open: true, role });
  };

  const tableColumns = [
    {
      key: 'name',
      header: 'Role',
      sortable: true,
      getValue: (role) => role.name,
      render: (role) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight="medium">
            {role.name}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      getValue: (role) => role.code,
      render: (role) => <Chip label={role.code} size="small" variant="outlined" />,
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      getValue: (role) => role.description,
      render: (role) => (
        <Typography variant="body2" color="textSecondary">
          {role.description || '—'}
        </Typography>
      ),
    },
    {
      key: 'user_count',
      header: 'Users',
      align: 'center',
      sortable: true,
      getValue: (role) => role.user_count || 0,
      render: (role) => (
        <Chip
          icon={<PeopleIcon />}
          label={role.user_count || 0}
          size="small"
          color={role.user_count > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      key: 'type',
      header: 'Type',
      align: 'center',
      sortable: true,
      getValue: (role) => role.is_system_role ? 'System' : 'Custom',
      render: (role) =>
        role.is_system_role ? (
          <Chip icon={<LockIcon />} label="System" size="small" color="warning" />
        ) : (
          <Chip label="Custom" size="small" color="info" />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'center',
      render: (role) => (
        <>
          <Tooltip title="Edit Permissions">
            <IconButton
              size="small"
              onClick={() => setPermissionDialog({ open: true, role })}
            >
              <SecurityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!role.is_system_role && (
            <>
              <Tooltip title="Edit Role">
                <IconButton size="small" onClick={() => openEditDialog(role)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clone Role">
                <IconButton size="small" onClick={() => openCloneDialog(role)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Role">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => openDeleteConfirm(role)}
                  disabled={role.user_count > 0}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {role.is_system_role && (
            <Tooltip title="Clone Role">
              <IconButton size="small" onClick={() => openCloneDialog(role)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Role Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setFormData({ code: '', name: '', description: '' });
            setCreateDialog(true);
          }}
        >
          Create Role
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            Roles define sets of permissions that can be assigned to users.
            <strong> System roles</strong> are available to all tenants.
            <strong> Tenant roles</strong> are specific to {tenant?.name || 'your organization'}.
          </Typography>
        </CardContent>
      </Card>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            icon={<LockIcon />}
            iconPosition="start"
            label={`System Roles (${systemRoles.length})`}
          />
          <Tab
            icon={<SecurityIcon />}
            iconPosition="start"
            label={`Tenant Roles (${tenantRoles.length})`}
          />
        </Tabs>
      </Paper>

      <DataTable
        columns={tableColumns}
        data={displayedRoles}
        pagination={pagination}
        sort={sort}
        emptyMessage={
          activeTab === 0
            ? 'No system roles found'
            : 'No tenant roles yet. Click "Create Role" to add one.'
        }
      />

      {/* Create Role Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Role</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            helperText="Unique identifier (e.g., senior_analyst)"
            required
          />
          <TextField
            fullWidth
            label="Role Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            helperText="Display name (e.g., Senior Analyst)"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          {!isMasterTenant && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This role will be specific to {tenant?.name || 'your tenant'}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateRole}
            disabled={actionLoading || !formData.code || !formData.name}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, role: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role Code"
            value={formData.code}
            margin="normal"
            disabled
            helperText="Code cannot be changed"
          />
          <TextField
            fullWidth
            label="Role Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, role: null })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateRole}
            disabled={actionLoading || !formData.name}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Role Dialog */}
      <Dialog open={cloneDialog.open} onClose={() => setCloneDialog({ open: false, role: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Role: {cloneDialog.role?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will create a copy of the role with all its permissions.
          </Alert>
          <TextField
            fullWidth
            label="New Role Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            helperText="Unique identifier for the new role"
            required
          />
          <TextField
            fullWidth
            label="New Role Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialog({ open: false, role: null })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCloneRole}
            disabled={actionLoading || !formData.code || !formData.name}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Dialog */}
      <ConfirmDialog {...confirm.dialogProps} loading={actionLoading} />

      {/* Permission Matrix Dialog */}
      <Dialog
        open={permissionDialog.open}
        onClose={() => setPermissionDialog({ open: false, role: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Edit Permissions: {permissionDialog.role?.name}
            {permissionDialog.role?.is_system_role && (
              <Chip label="System Role (Read-Only)" size="small" color="warning" sx={{ ml: 2 }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {permissionDialog.role && (
            <RolePermissionMatrix
              roleId={permissionDialog.role.id}
              readOnly={permissionDialog.role.is_system_role}
              onSave={(permissions) => handleSavePermissions(permissionDialog.role.id, permissions)}
              onCancel={() => setPermissionDialog({ open: false, role: null })}
              loading={actionLoading}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
